// Libraries
import React from 'react';
import {css} from 'aphrodite/no-important';
// Core
import CoreMetrics, {createExpandableMediaQueryStylesheet} from '../core/Metrics';
import CoreStyles from '../core/Styles';
import {TitleText, HeadingMediumText, BodyText, CaptionText, LabelMediumText} from '../core/Type';
import {FixedSpace} from '../core/Spacing';
import Glyph from '../core/Glyph';
// Controls
import Button from '../controls/Button';
// Layouts
import {OneContentColumnLayout, TwoColumnLayout, ThreeColumnLayout} from '../layouts/Grid';
// Modules
import SiteBar from '../modules/SiteBar';
import SiteFooter from '../modules/SiteFooter';

export default class MarketingPage extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            page: {
                display: "flex",
                flexDirection: "column-reverse",
            },
            heroModule: {
                width: "100%",
                paddingTop: CoreMetrics.ElementSpacing.XLarge(),
                paddingBottom: CoreMetrics.ElementSpacing.XLarge(),
                backgroundColor: CoreMetrics.Color.Neutral.OffWhite,
            },
            centeredContent: {
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
            },
        });

        return (
            <div className={css(styles.page)}>
                <SiteFooter />
                <div className={css(styles.heroModule)}>
                    <OneContentColumnLayout column1={(
                        <div className={css(styles.centeredContent)}>
                            <HeadingMediumText>Start differentiating your classroom, today.</HeadingMediumText>
                            <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                            <Button title="Continue with Google" />
                            <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.Medium} />
                            <Button title="Sign up with email" />
                        </div>
                    )} />
                </div>
                <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                <OneContentColumnLayout column1={(
                    <div className={css(styles.centeredContent)}>
                        <HeadingMediumText>Join over 1 million teachers from around the world</HeadingMediumText>
                        <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                        <BodyText>I have found Khan Academy to be a great option for homework. It has freed up valuable class time. Instead of making copies, correcting paperwork, and returning paperwork, I have more time to spend with my students teaching them how to write. Lots of benefits.</BodyText>
                        <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.Medium} />
                        <CaptionText>Kimberly Jones, 7th and 8th grade English language arts, Somerset Middle School</CaptionText>
                    </div>
                )} />
                <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                <div className={css(CoreStyles.hairlineStrokeBottom)}>
                    <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                    <TwoColumnLayout column1={(
                        <div className={css(styles.centeredContent)}>
                            <Glyph size={Glyph.Size.XXLarge} />
                        </div>
                    )} column2={(
                        <div>
                            <HeadingMediumText>Easy to use, and 100% free forever</HeadingMediumText>
                            <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                            <BodyText>&middot; Integrated with Google Classroom for easy setup</BodyText>
                            <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.Medium} />
                            <BodyText>&middot; Available in multiple languages</BodyText>
                            <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.Medium} />
                            <BodyText>&middot; Works with the technology you have, whether it’s shared devices, 1:1, tech lab, or mobile phones</BodyText>
                        </div>
                    )} />
                    <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                </div>
                <div className={css(styles.heroModule)}>
                    <TwoColumnLayout column1={(
                        <div>
                            <HeadingMediumText>Students get instant feedback and build a growth mindset</HeadingMediumText>
                            <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                            <BodyText>When students practice their skills on Khan Academy, they get immediate feedback, helpful hints, and encouragement to keep trying. So students take ownership of their learning.</BodyText>
                            <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.Medium} />
                            <BodyText>And what about results? We’re delighted to share that multiple studies show students get higher-than-expected learning outcomes when studying with Khan Academy.</BodyText>
                        </div>
                    )} column2={(
                        <div className={css(styles.centeredContent)}>
                            <Glyph size={Glyph.Size.XXLarge} />
                        </div>
                    )} />
                </div>
                <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                <ThreeColumnLayout column1={(
                    <div className={css(styles.centeredContent)}>
                        <Glyph size={Glyph.Size.XXLarge} />
                        <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                        <LabelMediumText>1. Find standards-aligned content</LabelMediumText>
                        <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XSmall} />
                        <BodyText>Khan Academy offers K-14 content in Math, Science, History, Economics and more.</BodyText>
                    </div>
                )} column2={(
                    <div className={css(styles.centeredContent)}>
                        <Glyph size={Glyph.Size.XXLarge} />
                        <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                        <LabelMediumText>2. Make assignments</LabelMediumText>
                        <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XSmall} />
                        <BodyText>More than instructional videos and articles, Khan Academy offers thousands of interactive practice problems.</BodyText>
                    </div>
                )} column3={(
                    <div className={css(styles.centeredContent)}>
                        <Glyph size={Glyph.Size.XXLarge} />
                        <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                        <LabelMediumText>3. Track student progress</LabelMediumText>
                        <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XSmall} />
                        <BodyText>Powerful reports show you student activity, gaps and progress.</BodyText>
                    </div>
                )} />
                <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                <OneContentColumnLayout column1={(
                    <div className={css(styles.centeredContent)}>
                        <HeadingMediumText>How Khan Academy works</HeadingMediumText>
                        <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XSmall} />
                        <BodyText>Provide remediation, grade-level practice and enrichment today.</BodyText>
                    </div>
                )} />
                <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.XLarge} />
                <div className={css(styles.heroModule)}>
                    <TwoColumnLayout column1={(
                        <div>
                            <BodyText>Khan Academy for teachers</BodyText>
                            <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.Medium} />
                            <TitleText>Help every student succeed with personalized practice. 100% free.</TitleText>
                        </div>
                    )} column2={(
                        <div>
                            <Button title="Continue with Google" />
                            <FixedSpace variant={FixedSpace.Variant.Vertical} size={FixedSpace.Size.Medium} />
                            <Button title="Sign up with email" />
                        </div>
                    )} />
                </div>
                <SiteBar />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/demo/MarketingPage.jsx